'use client';

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import type { ShippingEstimateObject } from 'components/ring-air/utils/types';
import { BatteryLifeSection } from 'components/landing/v3/BatteryLifeSection';
import { EmeraldArcSection } from 'components/landing/v3/EmeraldArcSection';
import { HomepageLenis } from 'components/landing/v3/HomepageLenis';
import { PartnerLogoMarquee } from 'components/landing/v3/PartnerLogoMarquee';
import { RingMeasuresSection } from 'components/landing/v3/RingMeasuresSection';
import { JsonLd } from 'components/shared';
import TypekitFonts from 'components/shared/TypekitFonts';
import { getAssetUrl } from 'lib/config/assetUrls';
import { generateBreadcrumbSchema } from 'lib/seo/schema';
import { generateProductSchema } from 'lib/seo/schema/product';
import { geist, jetbrainsMono } from 'utils/fonts';
import { RingProUsCost } from './cost/RingProUsCost';
import { RingProUsFit } from './fit/RingProUsFit';
import {
  RING_PRO_US_HERO_VISIBILITY,
  RingProUsHero,
} from './hero/RingProUsHero';
import { RingProUsPay } from './pay/RingProUsPay';
import { RingProUsPress } from './press/RingProUsPress';
import { RingProUsScienceBanner } from './science/RingProUsScienceBanner';
import { RingProUsSpotlight } from './spotlight/RingProUsSpotlight';
import { RingProUsStats } from './stats/RingProUsStats';
import { RingProUsStatsDisclaimer } from './stats/RingProUsStatsDisclaimer';
import { RingProUsTalk } from './talk/RingProUsTalk';
import styles from './UsHomePage.module.css';

type UsHomePageProps = {
  /** Ring PRO base price in USD, resolved on the server. */
  price: number;
  /** Formatted price line, e.g. "$479". */
  priceLabel: string;
  worldSleep?: ReactNode;
  estimates?: ShippingEstimateObject | null;
};

/**
 * US-only Ring PRO homepage. Section order is the desktop frame order at
 * every width; smaller breakpoints only reflow. No campaign carousel.
 */
export const UsHomePage = ({
  price,
  priceLabel,
  worldSleep,
  estimates,
}: UsHomePageProps) => {
  const t = useTranslations('homepage');

  useEffect(() => {
    document.body.classList.add('us-home-hide-chat');
    return () => document.body.classList.remove('us-home-hide-chat');
  }, []);

  const schemas = [
    generateBreadcrumbSchema([
      {
        name: t('homepage.breadcrumb.home'),
        url: 'https://www.ultrahuman.com',
      },
    ]),
    generateProductSchema({
      name: t('homepage.ringProUs.seo.productName'),
      description: t('homepage.ringProUs.seo.description'),
      imageUrl: getAssetUrl('/web_v2/meta-assets/uh-og-image.png'),
      price: price > 0 ? price : undefined,
      currency: 'USD',
      country: 'US',
      url: 'https://www.ultrahuman.com/us/',
      omitPriceValidUntil: true,
      includeShippingDetails: false,
    }),
  ];

  return (
    <main
      id="main-content"
      role="main"
      className={`${styles.pageMain} ${styles.usPage} ${jetbrainsMono.variable} ${geist.variable}`}
      aria-label={t('homepage.aria.pageMain')}
    >
      <JsonLd data={schemas} />
      <HomepageLenis />
      <TypekitFonts kits={['futura', 'dharma']} />
      <RingProUsHero
        price={priceLabel}
        visibility={RING_PRO_US_HERO_VISIBILITY}
        aria-label={t('homepage.ringProUs.aria.section')}
      />
      <RingProUsStats />
      <RingProUsPress />
      <BatteryLifeSection aria-label={t('homepage.batteryLife.aria.section')} />
      <RingMeasuresSection
        variant="us"
        dark
        footer={<RingProUsScienceBanner />}
        aria-label={t('homepage.ringMeasures.aria.section')}
      />
      <RingProUsSpotlight price={priceLabel} />
      <RingProUsFit estimates={estimates} />
      <RingProUsCost price={price} />
      <RingProUsPay />
      <EmeraldArcSection
        aria-label={t('homepage.emerald.aria.updateSection')}
      />
      {worldSleep}
      <RingProUsTalk />
      <PartnerLogoMarquee
        aria-label={t('homepage.scienceLibrary.aria.partners')}
        className={styles.hideOnPhone}
      />
      <RingProUsStatsDisclaimer />
    </main>
  );
};

export default UsHomePage;
